import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Loader2, RotateCcw, AlertTriangle } from 'lucide-react';

export default function OrderRefundPanel({ order, onRefunded }) {
  const [mode, setMode] = useState('idle'); // idle | form | confirm
  const [refundAmount, setRefundAmount] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const alreadyRefunded = order.refunded_amount || 0;
  const remaining = (order.amount || 0) - alreadyRefunded;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(refundAmount);
    if (!amt || amt <= 0 || amt > remaining) { setError(`Amount must be between £0.01 and £${remaining.toFixed(2)}`); return; }
    if (!reason.trim()) { setError('Please provide a reason.'); return; }
    setMode('confirm');
    setError('');
  };

  const handleConfirm = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await base44.functions.invoke('adminIssueRefund', {
        orderId: order.id,
        refundAmount: parseFloat(refundAmount),
        reason,
      });
      if (res.data?.success) {
        const cn = res.data.creditNoteNumber ? ` Credit note ${res.data.creditNoteNumber} issued and emailed.` : '';
        setSuccess(`Refund of £${parseFloat(refundAmount).toFixed(2)} processed.${cn}`);
        setMode('idle');
        setRefundAmount('');
        setReason('');
        onRefunded?.();
      } else {
        setError(res.data?.error || 'Refund failed.');
        setMode('form');
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Refund failed.');
      setMode('form');
    }
    setSaving(false);
  };

  if (remaining <= 0) {
    return <p className="text-white/30 text-xs text-center py-4">This order has been fully refunded.</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-xs">
        <span className="text-white/40">Eligible for refund</span>
        <span className="text-white font-bold">£{remaining.toFixed(2)}</span>
      </div>

      {success && <p className="text-green-400 text-xs bg-green-400/10 border border-green-400/20 px-3 py-2">{success}</p>}
      {error && <p className="text-red-400 text-xs bg-red-400/10 border border-red-400/20 px-3 py-2">{error}</p>}

      {mode === 'idle' && (
        <button onClick={() => setMode('form')}
          className="flex items-center gap-1.5 text-orange-400 hover:text-orange-300 text-[10px] font-bold tracking-widest transition-colors">
          <RotateCcw className="w-3.5 h-3.5" /> ISSUE REFUND
        </button>
      )}

      {mode === 'form' && (
        <form onSubmit={handleSubmit} className="space-y-2 border border-orange-500/20 p-3 bg-orange-500/5">
          <div>
            <p className="text-white/30 text-[9px] font-bold tracking-widest mb-1">REFUND AMOUNT (£)</p>
            <input
              type="number" step="0.01" min="0.01" max={remaining}
              value={refundAmount} onChange={e => setRefundAmount(e.target.value)}
              placeholder={`Max £${remaining.toFixed(2)}`}
              className="w-full bg-[#111115] border border-white/10 text-white text-xs h-8 px-3 focus:outline-none focus:border-orange-500/60 placeholder:text-white/20"
            />
          </div>
          <div>
            <p className="text-white/30 text-[9px] font-bold tracking-widest mb-1">REASON *</p>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2}
              placeholder="Reason for refund…"
              className="w-full bg-[#111115] border border-white/10 text-white text-xs px-3 py-2 resize-none focus:outline-none focus:border-orange-500/60 placeholder:text-white/20" />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="flex items-center gap-1.5 bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 border border-orange-500/20 text-[10px] font-bold tracking-widest px-3 py-1.5 transition-colors">
              <RotateCcw className="w-3 h-3" /> CONTINUE
            </button>
            <button type="button" onClick={() => { setMode('idle'); setError(''); }} className="text-white/30 text-[10px] font-bold px-3 py-1.5 hover:text-white transition-colors">
              CANCEL
            </button>
          </div>
        </form>
      )}

      {mode === 'confirm' && (
        <div className="border border-red-500/30 p-3 bg-red-500/5 space-y-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-red-400 text-xs font-bold">Confirm refund of £{parseFloat(refundAmount).toFixed(2)}</p>
              <p className="text-white/40 text-[10px] mt-1">This will charge back via Square, issue a branded credit note (PDF) and email it to the customer. This cannot be undone.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleConfirm} disabled={saving}
              className="flex items-center gap-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/20 text-[10px] font-bold tracking-widest px-3 py-1.5 transition-colors disabled:opacity-40">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
              {saving ? 'PROCESSING…' : 'CONFIRM REFUND'}
            </button>
            <button onClick={() => setMode('form')} disabled={saving} className="text-white/30 text-[10px] font-bold px-3 py-1.5 hover:text-white transition-colors disabled:opacity-40">
              BACK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}